import React, { useState, useCallback } from "react";
import { useHistory, useLocation, Link } from "react-router-dom";
import "../../auth.css";

const VERIFY_OTP_URL = process.env.REACT_APP_MFA_POC_VERIFY_OTP_URL; // MFA: backend endpoint

function safeErrorMessage(e) {
  if (!e) return "Something went wrong. Please try again.";
  if (typeof e === "string") return e;
  if (e.message) return e.message;
  return String(e);
}

export default function MfaPocVerifyCode() {
  const history = useHistory();
  const location = useLocation();

  const sp = new URLSearchParams(location.search);
  const initialEmail = (sp.get("email") || "").trim();

  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  const onSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!email || !code) {
        setError("Please enter both email and code.");
        return;
      }
      if (!VERIFY_OTP_URL) {
        setError("Reset service is not configured. Please contact support."); // MFA: missing env guard
        return;
      }

      setBusy(true);
      setError("");
      setInfo("");

      try {
        const payload = {
          username: email.trim(),
          email: email.trim(),
          otp: code.trim(), // lambda also accepts "code", but "otp" is explicit
        };

        const res = await fetch(VERIFY_OTP_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify(payload),
        });

        let data = null;
        try {
          data = await res.json();
        } catch {
          // ignore JSON parse errors
        }

        if (!res.ok) {
          const msg =
            (data && data.message) || "The code is invalid or has expired.";
          setError(msg);
          return;
        }

        const msg =
          (data && data.message) ||
          "If your code is correct, a reset link has been sent.";
        setInfo(msg);

        // MFA: we *do not* auto-redirect to complete page.
        // The user must click the reset link from their email, which contains token.
        // Optional: keep them on page, but give a hint.
      } catch (ex) {
        setError(safeErrorMessage(ex));
      } finally {
        setBusy(false);
      }
    },
    [email, code, VERIFY_OTP_URL]
  );

  return (
    <div className="ph-auth-wrap">
      <div className="ph-auth-card">
        <h1 className="ph-auth-title">MFA: Verify Code</h1>
        <p className="ph-hint">
          Enter the one-time code sent to your email. If valid, we’ll email you a
          reset link.
        </p>

        {info && (
          <div className="ph-note" role="status">
            {info}
          </div>
        )}
        {error && (
          <div className="ph-error" role="alert">
            {error}
          </div>
        )}

        <form onSubmit={onSubmit}>
          <label className="ph-label" htmlFor="mfa2-email">
            Email
          </label>
          <input
            id="mfa2-email"
            className="ph-field"
            type="email"
            placeholder="name@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="username"
            disabled={busy}
          />

          <label className="ph-label" htmlFor="mfa2-code">
            One-time code
          </label>
          <input
            id="mfa2-code"
            className="ph-field"
            type="text"
            inputMode="numeric"
            pattern="\d*"
            placeholder="Enter your code"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\s+/g, ""))}
            disabled={busy}
          />

          <button
            className="ph-button"
            type="submit"
            disabled={busy || !email || !code}
          >
            {busy ? "Verifying…" : "Verify code"}
          </button>
        </form>

        <p className="ph-hint" style={{ marginTop: 12 }}>
          <Link className="ph-link" to="/auth/mfa/request">
            Start over
          </Link>
          {"  ·  "}
          <Link className="ph-link" to="/signin">
            Back to Sign In
          </Link>
        </p>
      </div>
    </div>
  );
}
